import React from 'react';
import clsx from 'clsx';

/**
 * Canonical badge / pill primitive — matches the status-badge pattern
 * in STYLE_GUIDE §10.  Use for clinical-state labels, scope tags, and
 * overlines.  For interactive pills (segmented controls), use buttons
 * styled per §7 instead — Badge is non-interactive by design.
 */

type Variant = 'success' | 'warning' | 'error' | 'info' | 'neutral' | 'overline';

interface Props {
    variant?: Variant;
    className?: string;
    children: React.ReactNode;
}

const VARIANT_CLASSES: Record<Variant, string> = {
    // UI semantic variants — backed by the --mld-success/warning/danger/info
    // tokens (STYLE_GUIDE §1) so all four route through the white-labeling
    // layer.  Tonal pattern: 10% tinted bg + full-color text for legibility.
    success: 'bg-mld-success/10 text-mld-success',
    warning: 'bg-mld-warning/10 text-mld-warning',
    error: 'bg-mld-danger/10 text-mld-danger',
    info: 'bg-mld-info/10 text-mld-info',
    neutral: 'bg-gray-100 text-gray-600',
    overline:
        'border border-mld-panel-border bg-mld-background/60 text-mld-muted-text uppercase tracking-wider',
};

const Badge: React.FC<Props> = ({variant = 'neutral', className, children}) => {
    const isOverline = variant === 'overline';
    return (
        <span
            className={clsx(
                'inline-flex items-center gap-1 rounded-full font-medium',
                isOverline ? 'px-2.5 py-0.5 text-[10px]' : 'px-2.5 py-0.5 text-xs',
                VARIANT_CLASSES[variant],
                className
            )}>
            {children}
        </span>
    );
};

export default Badge;
